<template>
  <div class="dynamic-proxy-page dark-theme">
    <!-- Navigation -->
    <div class="nav-container" :class="{ 'scrolled': isScrolled }">
      <nav class="modern-nav">
        <div class="nav-brand" @click="$router.push('/')">
          <img src="/logo.png" alt="logo" class="brand-logo-img" />
          <span class="brand-name">spaceproxy</span>
        </div>

        <div class="nav-links desktop-only">
          <router-link to="/">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy" class="router-link-exact-active">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
        </div>

        <div class="nav-actions">
          <a-dropdown :trigger="['click']" placement="bottomCenter">
            <button class="action-icon-btn">
              <a-icon type="global" />
              <span class="lang-text">{{ $i18n.locale === 'zh-CN' ? '中' : 'EN' }}</span>
            </button>
            <a-menu slot="overlay" @click="handleLangClick" class="custom-dark-menu">
              <a-menu-item key="zh-CN">🇨🇳 简体中文</a-menu-item>
              <a-menu-item key="en-US">🇺🇸 English</a-menu-item>
            </a-menu>
          </a-dropdown>

          <template v-if="!isLoggedIn">
            <button class="btn-ghost desktop-only" @click="$router.push('/login')">{{ $t('menu.login') }}</button>
            <button class="btn-primary desktop-only" @click="$router.push('/register')">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <a-dropdown :trigger="['hover']" placement="bottomRight">
              <button class="user-pill desktop-only" @click="$router.push('/dashboard/auth')">
                <div class="u-avatar"><a-icon type="user" /></div>
                <span class="u-name">{{ username }}</span>
              </button>
              <a-menu slot="overlay" class="custom-dark-menu">
                <a-menu-item key="dashboard" @click="$router.push('/dashboard/auth')">
                  <a-icon type="appstore" /> {{ $t('menu.dashboard') }}
                </a-menu-item>
                <a-menu-divider />
                <a-menu-item key="logout" @click="handleLogout" class="danger-text">
                  <a-icon type="logout" /> {{ $t('menu.logout') }}
                </a-menu-item>
              </a-menu>
            </a-dropdown>
          </template>

          <button class="action-icon-btn mobile-only" @click="showMobileMenu = true">
            <a-icon type="menu" />
          </button>
        </div>
      </nav>
    </div>

    <!-- Mobile Drawer -->
    <a-drawer
      placement="right"
      :visible="showMobileMenu"
      @close="showMobileMenu = false"
      :closable="false"
      width="100%"
      wrapClassName="dark-mobile-drawer"
    >
      <div class="drawer-inner">
        <div class="d-header">
          <div class="nav-brand">
            <img src="/logo.png" alt="logo" class="brand-logo-img" />
            <span class="brand-name">spaceproxy</span>
          </div>
          <button class="close-btn" @click="showMobileMenu = false">
            <a-icon type="close" />
          </button>
        </div>
        <div class="d-menu">
          <router-link to="/" @click.native="showMobileMenu = false">{{ $t('menu.home') }}</router-link>
          <router-link to="/dynamic-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.dynamicProxy') }}</router-link>
          <!-- <router-link to="/static-residential-proxy" @click.native="showMobileMenu = false">{{ $t('menu.staticProxyPage') }}</router-link> -->
          <router-link to="/help-center" @click.native="showMobileMenu = false">{{ $t('menu.helpCenter') }}</router-link>
          <a @click="toggleMobileLang"><a-icon type="global" /> {{ $i18n.locale === 'zh-CN' ? 'Switch to English' : '切换到中文' }}</a>
        </div>
        <div class="d-footer">
          <template v-if="!isLoggedIn">
            <button class="btn-ghost full-width" @click="$router.push('/login'); showMobileMenu = false">{{ $t('menu.login') }}</button>
            <button class="btn-primary full-width mt-4" @click="$router.push('/register'); showMobileMenu = false">{{ $t('menu.register') }}</button>
          </template>
          <template v-else>
            <div class="d-user">
              <div class="u-avatar"><a-icon type="user" /></div>
              <span>{{ username }}</span>
            </div>
            <button class="btn-ghost full-width mt-4" @click="$router.push('/dashboard/auth'); showMobileMenu = false">{{ $t('menu.dashboard') }}</button>
            <button class="btn-danger full-width mt-4" @click="handleLogout(); showMobileMenu = false">{{ $t('menu.logout') }}</button>
          </template>
        </div>
      </div>
    </a-drawer>

    <!-- Product Hero -->
    <div class="product-hero">
      <div class="hero-bg-animated">
        <div class="glow-orb glow-1"></div>
        <div class="glow-orb glow-2"></div>
      </div>
      <div class="hero-content">
        <h1>{{ $t('dynamicProxy.hero.title') }}</h1>
        <p class="hero-subtitle" v-html="$t('dynamicProxy.hero.subtitle')"></p>
        <div class="hero-actions">
          <a-button type="primary" size="large" class="cta-btn" @click="$router.push('/login')">{{ $t('dynamicProxy.hero.buyNow') }}</a-button>
          <!-- <a-button size="large" ghost class="contact-btn" @click="handleContactClick">{{ $t('dynamicProxy.hero.contactSales') }}</a-button> -->
        </div>
      </div>
    </div>

    <!-- Stats Section -->
    <div class="stats-section scroll-reveal">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item">
            <div class="s-val text-gradient">{{ $t('dynamicProxy.stats.ipCount') }}</div>
            <div class="s-lbl">{{ $t('dynamicProxy.stats.ipLabel') }}</div>
          </div>
          <div class="stat-item">
            <div class="s-val text-gradient">{{ $t('dynamicProxy.stats.successRate') }}</div>
            <div class="s-lbl">{{ $t('dynamicProxy.stats.successLabel') }}</div>
          </div>
          <div class="stat-item">
            <div class="s-val text-gradient">{{ $t('dynamicProxy.stats.speed') }}</div>
            <div class="s-lbl">{{ $t('dynamicProxy.stats.speedLabel') }}</div>
          </div>
          <div class="stat-item">
            <div class="s-val text-gradient">{{ $t('dynamicProxy.stats.country') }}</div>
            <div class="s-lbl">{{ $t('dynamicProxy.stats.countryLabel') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Key Features -->
    <div class="features-section">
      <h2 class="section-title">{{ $t('dynamicProxy.features.title') }}</h2>
      <a-row :gutter="[24, 24]">
        <a-col :md="8" :sm="24" :xs="24" v-for="(feature, index) in features" :key="index">
          <div class="feature-item">
            <a-icon :type="feature.icon" class="feature-icon" />
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </a-col>
      </a-row>
    </div>

    <!-- Use Cases Section -->
    <div class="use-cases-section">
      <div class="container">
        <h2 class="section-title">{{ $t('dynamicProxy.useCases.title') }}</h2>
        <p class="section-desc">{{ $t('dynamicProxy.useCases.desc') }}</p>
        
        <div class="use-cases-grid">
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="shopping" /></div>
            <h3>{{ $t('dynamicProxy.useCases.c1Title') }}</h3>
            <p>{{ $t('dynamicProxy.useCases.c1Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="search" /></div>
            <h3>{{ $t('dynamicProxy.useCases.c2Title') }}</h3>
            <p>{{ $t('dynamicProxy.useCases.c2Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="message" /></div>
            <h3>{{ $t('dynamicProxy.useCases.c3Title') }}</h3>
            <p>{{ $t('dynamicProxy.useCases.c3Desc') }}</p>
          </div>
          <div class="uc-card">
            <div class="uc-icon"><a-icon type="database" /></div>
            <h3>{{ $t('dynamicProxy.useCases.c4Title') }}</h3>
            <p>{{ $t('dynamicProxy.useCases.c4Desc') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Plans - Glassmorphism -->
    <div class="pricing-section scroll-reveal">
      <div class="container">
        <h2 class="section-title">{{ $t('dynamicProxy.pricing.title') }}</h2>
        <div class="pricing-grid">
          <div class="price-card" v-for="(plan, index) in plans" :key="index" :class="{'featured': index === 1}">
            <div class="p-head">
              <h3>{{ plan.name }}</h3>
              <div class="price">
                <span class="currency">$</span>{{ plan.price }}<span class="unit">/GB</span>
              </div>
            </div>
            <div class="p-body">
              <ul>
                <li v-for="(item, i) in plan.features" :key="i">
                  <a-icon type="check-circle" theme="filled" />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>
            <div class="p-foot">
              <button class="btn-primary" @click="$router.push('/dashboard/auth')">{{ $t('dynamicProxy.pricing.startNow') }}</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Testimonials Section -->
    <div class="testimonials-section scroll-reveal">
      <div class="container">
        <h2 class="section-title">{{ $t('dynamicProxy.testimonials.title') }}</h2>
        <p class="section-desc">{{ $t('dynamicProxy.testimonials.desc') }}</p>
        
        <div class="testi-grid">
          <div class="testi-card">
            <div class="quote-icon"><a-icon type="quote-left" /></div>
            <p class="t-text">"{{ $t('dynamicProxy.testimonials.t1Text') }}"</p>
            <div class="t-author">
              <div class="a-avatar">Z</div>
              <div class="a-info">
                <h4>{{ $t('dynamicProxy.testimonials.t1Author') }}</h4>
                <span>{{ $t('dynamicProxy.testimonials.t1Role') }}</span>
              </div>
            </div>
          </div>
          <div class="testi-card">
            <div class="quote-icon"><a-icon type="quote-left" /></div>
            <p class="t-text">"{{ $t('dynamicProxy.testimonials.t2Text') }}"</p>
            <div class="t-author">
              <div class="a-avatar" style="background: #3b82f6;">D</div>
              <div class="a-info">
                <h4>{{ $t('dynamicProxy.testimonials.t2Author') }}</h4>
                <span>{{ $t('dynamicProxy.testimonials.t2Role') }}</span>
              </div>
            </div>
          </div>
          <div class="testi-card">
            <div class="quote-icon"><a-icon type="quote-left" /></div>
            <p class="t-text">"{{ $t('dynamicProxy.testimonials.t3Text') }}"</p>
            <div class="t-author">
              <div class="a-avatar" style="background: #10b981;">W</div>
              <div class="a-info">
                <h4>{{ $t('dynamicProxy.testimonials.t3Author') }}</h4>
                <span>{{ $t('dynamicProxy.testimonials.t3Role') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="dark-footer">
      <div class="container">
        <div class="f-grid">
          <div class="f-brand">
            <div class="nav-brand">
              <img src="/logo.png" alt="logo" class="brand-logo-img" />
              <span class="brand-name">spaceproxy</span>
            </div>
            <p>{{ $t('home.footer.desc') }}</p>
          </div>
          <div class="f-links">
            <div class="f-col">
              <h4>{{ $t('home.footer.products') }}</h4>
              <router-link to="/dynamic-residential-proxy">{{ $t('menu.dynamicProxy') }}</router-link>
              <router-link to="/static-residential-proxy">{{ $t('menu.staticProxyPage') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.support') }}</h4>
              <router-link to="/help-center">{{ $t('menu.helpCenter') }}</router-link>
            </div>
            <div class="f-col">
              <h4>{{ $t('home.footer.contact') }}</h4>
              <div class="contact-item">
                <a-icon type="wechat" />
                <span></span>
              </div>
              <!-- <div class="contact-item">
                <a-icon type="qq" />
                <span>QQ: </span>
              </div> -->
            </div>
          </div>
        </div>
        <div class="f-bottom">
          <p>{{ $t('home.footer.copyright', { year: new Date().getFullYear() }) }}</p>
        </div>
      </div>
    </footer>

    <!-- Connect Float -->
    <!-- <div class="connect-float">
      <a href="tencent://message/?uin=1337007470" class="c-btn qq-btn">
        <a-icon type="qq" />
        <span class="c-tooltip">QQ</span>
      </a>
      <a href="javascript:void(0)" class="c-btn wechat-btn">
        <a-icon type="wechat" />
        <span class="c-tooltip">Q95082323</span>
      </a>
    </div> -->

    <!-- Back Top -->
    <a-back-top :visibilityHeight="300">
      <div class="back-top-btn">
        <a-icon type="arrow-up" />
      </div>
    </a-back-top>
  </div>
</template>

<script>
export default {
  name: 'DynamicResidentialProxy',
  data() {
    return {
      isLoggedIn: false,
      username: '',
      showMobileMenu: false,
      isScrolled: false
    };
  },
  computed: {
    features() {
      return [
        {
          title: this.$t('dynamicProxy.features.successRate'),
          description: this.$t('dynamicProxy.features.successRateDesc'),
          icon: 'check-circle'
        },
        {
          title: this.$t('dynamicProxy.features.concurrency'),
          description: this.$t('dynamicProxy.features.concurrencyDesc'),
          icon: 'thunderbolt'
        },
        {
          title: this.$t('dynamicProxy.features.location'),
          description: this.$t('dynamicProxy.features.locationDesc'),
          icon: 'environment'
        },
        {
          title: this.$t('dynamicProxy.features.rotation'),
          description: this.$t('dynamicProxy.features.rotationDesc'),
          icon: 'sync'
        },
        {
          title: this.$t('dynamicProxy.features.pure'),
          description: this.$t('dynamicProxy.features.pureDesc'),
          icon: 'safety'
        },
        {
          title: this.$t('dynamicProxy.features.speed'),
          description: this.$t('dynamicProxy.features.speedDesc'),
          icon: 'rocket'
        }
      ];
    },
    plans() {
      return [
        {
          name: this.$t('dynamicProxy.pricing.starter'),
          price: '6',
          features: [
            `10GB ${this.$t('dynamicProxy.pricing.traffic')}`,
            this.$t('dynamicProxy.pricing.unlimitedConcurrency'),
            this.$t('dynamicProxy.pricing.countryLocation'),
            this.$t('dynamicProxy.pricing.authMode'),
            this.$t('dynamicProxy.pricing.validity30')
          ]
        },
        {
          name: this.$t('dynamicProxy.pricing.pro'),
          price: '5',
          features: [
            `100GB ${this.$t('dynamicProxy.pricing.traffic')}`,
            this.$t('dynamicProxy.pricing.unlimitedConcurrency'),
            this.$t('dynamicProxy.pricing.cityLocation'),
            this.$t('dynamicProxy.pricing.authMode'),
            this.$t('dynamicProxy.pricing.validity90')
          ]
        },
        {
          name: this.$t('dynamicProxy.pricing.enterprise'),
          price: '4',
          features: [
            `1TB ${this.$t('dynamicProxy.pricing.traffic')}`,
            this.$t('dynamicProxy.pricing.unlimitedConcurrency'),
            this.$t('dynamicProxy.pricing.fullLocation'),
            this.$t('dynamicProxy.pricing.exclusiveSupport'),
            this.$t('dynamicProxy.pricing.validityPermanent')
          ]
        }
      ];
    }
  },
  mounted() {
    this.checkLoginStatus();
    this.initScrollReveal();
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 50;
    },
    handleLangClick({ key }) {
      this.$i18n.locale = key;
      localStorage.setItem('language', key);
    },
    toggleMobileLang() {
      const newLang = this.$i18n.locale === 'zh-CN' ? 'en-US' : 'zh-CN';
      this.$i18n.locale = newLang;
      localStorage.setItem('language', newLang);
    },
    handleContactClick() {
      this.$message.info('请联系');
    },
    checkLoginStatus() {
      const token = localStorage.getItem('Token');
      const username = localStorage.getItem('userName');
      if (token && username) {
        this.isLoggedIn = true;
        this.username = username;
      }
    },
    handleLogout() {
      localStorage.clear();
      this.isLoggedIn = false;
      this.username = '';
      this.$message.success('退出登录成功');
      this.$router.push('/login');
    },
    initScrollReveal() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      setTimeout(() => {
        const animatedElements = document.querySelectorAll('.features-section, .pricing-section, .scroll-reveal');
        animatedElements.forEach(el => {
          el.classList.add('scroll-reveal');
          observer.observe(el);
        });
      }, 100);
    }
  }
};
</script>

<style lang="less">
.user-dropdown-menu {
  padding: 8px;
  border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f7ff;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  min-width: 160px;
  
  .ant-dropdown-menu-item {
    border-radius: 8px;
    padding: 10px 16px;
    margin-bottom: 4px;
    font-weight: 500;
    color: #475569;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    
    .anticon {
      margin-right: 12px;
      font-size: 16px;
      color: #94a3b8;
      transition: all 0.2s;
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    
    &:hover {
      background: #eff6ff;
      color: #1890ff;
      transform: translateX(2px);
      
      .anticon {
        color: #1890ff;
      }
    }
  }

  .ant-dropdown-menu-item-divider {
    background-color: #f1f5f9;
    margin: 6px 0;
  }
  
  .logout-item {
    &:hover {
      background: #fff1f2;
      color: #ef4444;
      
      .anticon {
        color: #ef4444;
      }
    }
  }
}
</style>

<style scoped lang="less">
// 变量定义
@primary-color: #8b5cf6;
@secondary-color: #3b82f6;
@dark-bg: #09090b;
@card-bg: #18181b;
@border: #27272a;
@text-main: #f4f4f5;
@text-light: #a1a1aa;

.dynamic-proxy-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: @dark-bg;
  color: @text-main;
}

/* Common Layout */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

button {
  outline: none;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

/* Animations */
@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(139, 92, 246, 0); }
  100% { box-shadow: 0 0 0 0 rgba(139, 92, 246, 0); }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translate3d(0, 40px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.scroll-reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease-out;
}

.scroll-reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Navigation - Copied from Home */
.nav-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 20px;
  transition: all 0.4s ease;
  
  &.scrolled {
    padding: 10px 20px;
    
    .modern-nav {
      background: rgba(24, 24, 27, 0.8);
      backdrop-filter: blur(12px);
      border-color: @border;
      box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    }
  }
}

.modern-nav {
  max-width: 1200px;
  margin: 0 auto;
  height: 64px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  transition: all 0.4s ease;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  .brand-logo-img {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    object-fit: cover;
  }
  
  .brand-icon {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, @primary-color, @secondary-color);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    
    svg { width: 20px; height: 20px; }
  }
  
  .brand-name {
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: @text-main;
  }
}

.nav-links {
  display: flex;
  gap: 32px;
  
  a {
    color: @text-light;
    font-weight: 500;
    font-size: 15px;
    transition: color 0.3s;
    
    &:hover, &.router-link-exact-active {
      color: @text-main;
    }
  }
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-icon-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  color: @text-light;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 8px;
  transition: all 0.3s;
  
  &:hover {
    background: @card-bg;
    color: @text-main;
  }
}

.btn-ghost {
  color: @text-main;
  font-weight: 600;
  padding: 10px 20px;
  border-radius: 10px;
  transition: all 0.3s;
  
  &:hover { background: @card-bg; }
}

.btn-primary {
  background: linear-gradient(135deg, @primary-color, @secondary-color);
  color: #fff;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 10px;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
}

.user-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  background: @card-bg;
  border: 1px solid @border;
  padding: 4px 16px 4px 4px;
  border-radius: 20px;
  color: @text-main;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
  
  .u-avatar {
    width: 28px;
    height: 28px;
    background: @primary-color;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }
  
  &:hover {
    border-color: @primary-color;
  }
}

.mobile-only { display: none; }

/* Hero Section */
.product-hero {
  position: relative;
  background: @dark-bg;
  padding: 180px 0 100px;
  text-align: center;
  overflow: hidden;
  border-bottom: 1px solid @border;
}
.hero-bg-animated {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-image: 
    linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  mask-image: radial-gradient(ellipse at center, black 0%, transparent 80%);
}
.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.15;
  animation: float 10s infinite alternate;
}
.glow-1 {
  width: 600px;
  height: 600px;
  background: @primary-color;
  top: -200px;
  left: -100px;
}
.glow-2 {
  width: 600px;
  height: 600px;
  background: @secondary-color;
  bottom: -200px;
  right: -100px;
  animation-delay: -5s;
}

.hero-content {
  position: relative;
  z-index: 10;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 24px;
}
.hero-content h1 {
  font-size: 64px;
  font-weight: 900;
  color: #fff;
  margin-bottom: 24px;
  animation: fadeInUp 0.8s ease-out;
  line-height: 1.1;
  letter-spacing: -2px;
}
.hero-subtitle {
  font-size: 20px;
  color: @text-light;
  margin-bottom: 48px;
  line-height: 1.6;
  animation: fadeInUp 0.8s ease-out 0.2s backwards;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}
.hero-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}
.hero-actions .cta-btn {
  height: 56px;
  padding: 0 36px;
  font-size: 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, @primary-color, @secondary-color);
  border: none;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.3);
  animation: fadeInUp 0.8s ease-out 0.4s backwards;
}
.hero-actions .cta-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
}
.hero-actions .contact-btn {
  height: 56px;
  padding: 0 36px;
  font-size: 18px;
  font-weight: 600;
  border-radius: 14px;
  border: 1px solid @border;
  background: @card-bg;
  color: @text-main;
  animation: fadeInUp 0.8s ease-out 0.5s backwards;
}
.hero-actions .contact-btn:hover {
  background: rgba(255,255,255,0.05);
  border-color: @text-light;
  color: #fff;
}

/* Stats Section */
.stats-section {
  padding: 60px 0;
  background: #000;
  border-bottom: 1px solid @border;
}
.stats-grid {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.stat-item {
  flex: 1;
  min-width: 200px;
  text-align: center;
  
  .s-val {
    font-size: 48px;
    font-weight: 900;
    margin-bottom: 8px;
    font-variant-numeric: tabular-nums;
  }
  .s-lbl {
    color: @text-light;
    font-size: 15px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
}
.text-gradient {
  background: linear-gradient(135deg, @primary-color, @secondary-color);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Features Section - Bento Style */
.features-section {
  padding: 100px 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.section-title {
  text-align: center;
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 60px;
  color: #fff;
  letter-spacing: -1px;
}
.feature-item {
  text-align: left;
  padding: 32px;
  background: @card-bg;
  border-radius: 24px;
  transition: all 0.4s;
  height: 100%;
  border: 1px solid @border;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.feature-item::after {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(circle at top right, rgba(139, 92, 246, 0.1), transparent 60%);
  opacity: 0;
  transition: opacity 0.4s;
  z-index: 0;
}
.feature-item:hover {
  transform: translateY(-5px);
  border-color: rgba(139, 92, 246, 0.4);
}
.feature-item:hover::after {
  opacity: 1;
}
.feature-item:hover .feature-icon {
  background: @primary-color;
  color: #fff;
}
.feature-icon {
  width: 48px;
  height: 48px;
  background: rgba(255,255,255,0.05);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: @text-main;
  margin-bottom: auto;
  transition: all 0.4s;
  z-index: 1;
}
.feature-item h3 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  margin-top: 24px;
  color: #fff;
  z-index: 1;
}
.feature-item p {
  color: @text-light;
  line-height: 1.6;
  font-size: 15px;
  margin: 0;
  z-index: 1;
}

/* Use Cases Section */
.use-cases-section {
  padding: 100px 0;
  background: @dark-bg;
}
.section-desc {
  text-align: center;
  color: @text-light;
  font-size: 18px;
  margin: -40px 0 60px;
}
.use-cases-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.uc-card {
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  transition: all 0.3s;
  
  &:hover {
    transform: translateY(-5px);
    border-color: @primary-color;
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
    
    .uc-icon {
      background: @primary-color;
      color: #fff;
    }
  }
  
  .uc-icon {
    width: 64px;
    height: 64px;
    margin: 0 auto 24px;
    background: rgba(139, 92, 246, 0.1);
    color: @primary-color;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 32px;
    transition: all 0.3s;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 12px;
  }
  
  p {
    color: @text-light;
    font-size: 15px;
    line-height: 1.6;
    margin: 0;
  }
}

/* API Integration Section */
.api-integration-section {
  padding: 100px 0;
  background: #000;
  border-top: 1px solid @border;
}
.api-inner {
  display: flex;
  align-items: center;
  gap: 60px;
}
.api-content {
  flex: 1;
  
  h2 {
    font-size: 40px;
    font-weight: 800;
    color: #fff;
    margin-bottom: 24px;
    letter-spacing: -1px;
  }
  
  p {
    font-size: 18px;
    color: @text-light;
    line-height: 1.6;
  }
}
.api-code-window {
  flex: 1;
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0,0,0,0.5);
  
  .w-header {
    background: rgba(255,255,255,0.02);
    padding: 12px 16px;
    border-bottom: 1px solid @border;
    display: flex;
    align-items: center;
    gap: 8px;
    
    i { width: 12px; height: 12px; border-radius: 50%; background: #3f3f46; }
    i:nth-child(1) { background: #ef4444; }
    i:nth-child(2) { background: #eab308; }
    i:nth-child(3) { background: #22c55e; }
    
    .w-title {
      margin-left: 12px;
      color: @text-light;
      font-size: 13px;
      font-family: monospace;
    }
  }
  
  .w-body {
    padding: 24px;
    background: #09090b;
    overflow-x: auto;
    
    pre { margin: 0; }
    code {
      font-family: 'Fira Code', Consolas, monospace;
      font-size: 14px;
      color: #a1a1aa;
      line-height: 1.5;
    }
  }
}

/* Pricing Section - Glassmorphism */
.pricing-section {
  width: 100%;
  padding: 100px 24px;
  background: #000;
  border-top: 1px solid @border;
  border-bottom: 1px solid @border;
}
.pricing-grid {
  display: flex;
  justify-content: center;
  gap: 24px;
  max-width: 1000px;
  margin: 0 auto;
}
.price-card {
  flex: 1;
  background: rgba(24, 24, 27, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid @border;
  border-radius: 24px;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  transition: all 0.4s;
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(139, 92, 246, 0.4);
    box-shadow: 0 20px 40px rgba(0,0,0,0.5);
  }
  
  &.featured {
    background: linear-gradient(180deg, rgba(139, 92, 246, 0.1) 0%, rgba(24, 24, 27, 0.8) 100%);
    border-color: rgba(139, 92, 246, 0.5);
    transform: scale(1.05);
    z-index: 2;
    
    &:hover {
      transform: scale(1.05) translateY(-10px);
      border-color: @primary-color;
      box-shadow: 0 20px 40px rgba(139, 92, 246, 0.2);
    }
    
    .btn-primary {
      background: linear-gradient(135deg, @primary-color, @secondary-color);
      color: #fff;
      border: none;
    }
  }
}
.p-head {
  text-align: center;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 16px;
  }
  
  .price {
    font-size: 48px;
    font-weight: 800;
    color: #fff;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    
    .currency {
      font-size: 24px;
      vertical-align: super;
      margin-right: 4px;
      color: @text-light;
    }
    
    .unit {
      font-size: 16px;
      font-weight: 500;
      color: @text-light;
    }
  }
}
.p-body {
  flex: 1;
  margin-bottom: 40px;
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 16px;
      color: @text-main;
      font-size: 15px;
      line-height: 1.5;
      
      i {
        color: @primary-color;
        font-size: 18px;
        margin-top: 2px;
      }
    }
  }
}
.p-foot {
  .btn-primary {
    width: 100%;
    height: 48px;
    border-radius: 12px;
    background: transparent;
    border: 1px solid @border;
    color: #fff;
    font-weight: 600;
    transition: all 0.3s;
    
    &:hover {
      background: rgba(255,255,255,0.1);
      border-color: #fff;
    }
  }
}

/* Testimonials Section */
.testimonials-section {
  padding: 100px 0;
  background: @dark-bg;
}
.testi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}
.testi-card {
  background: @card-bg;
  border: 1px solid @border;
  border-radius: 20px;
  padding: 40px 32px;
  position: relative;
  transition: all 0.4s;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(139, 92, 246, 0.4);
    box-shadow: 0 10px 30px rgba(0,0,0,0.5);
  }
  
  .quote-icon {
    font-size: 40px;
    color: rgba(139, 92, 246, 0.2);
    margin-bottom: 24px;
  }
  
  .t-text {
    color: @text-main;
    font-size: 16px;
    line-height: 1.8;
    margin-bottom: 32px;
    min-height: 120px;
  }
  
  .t-author {
    display: flex;
    align-items: center;
    gap: 16px;
    border-top: 1px solid rgba(255,255,255,0.05);
    padding-top: 24px;
    
    .a-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      background: @primary-color;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 700;
    }
    
    .a-info {
      h4 {
        color: #fff;
        font-size: 16px;
        font-weight: 700;
        margin: 0 0 4px;
      }
      span {
        color: @text-light;
        font-size: 13px;
      }
    }
  }
}

/* Footer - Copied from Home */
.dark-footer {
  background: #000; border-top: 1px solid @border; padding: 80px 0 40px;
  
  .f-grid {
    display: flex; justify-content: space-between; gap: 40px; margin-bottom: 60px;
  }
  
  .f-brand {
    max-width: 300px;
    p { color: @text-light; line-height: 1.6; margin-top: 20px; }
  }
  
  .f-links {
    display: flex; gap: 80px;
    
    .f-col {
      display: flex; flex-direction: column; gap: 16px;
      h4 { color: #fff; font-weight: 700; font-size: 16px; margin: 0; }
      a { color: @text-light; transition: color 0.3s; &:hover { color: @primary-color; } }
      .contact-item {
        display: flex;
        align-items: center;
        gap: 8px;
        color: @text-light;
        font-size: 14px;
        i { color: @primary-color; font-size: 16px; }
      }
    }
  }
  
  .f-bottom {
    border-top: 1px solid @border; padding-top: 30px; text-align: center;
    p { color: @text-light; font-size: 14px; margin: 0; }
  }
}

/* Floating Connect */
.connect-float {
  position: fixed; right: 24px; bottom: 100px; z-index: 99;
  display: flex; flex-direction: column; gap: 12px;
  
  .c-btn {
    width: 50px; height: 50px;
    background: @card-bg; border: 1px solid @border; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: @text-main; font-size: 20px;
    position: relative; transition: all 0.3s;
    box-shadow: 0 4px 20px rgba(0,0,0,0.5);
    
    &.telegram-btn:hover { border-color: #0088cc; color: #0088cc; transform: scale(1.1); }
    &.qq-btn:hover { border-color: #12b7f5; color: #12b7f5; transform: scale(1.1); }
    &.wechat-btn:hover { border-color: #07c160; color: #07c160; transform: scale(1.1); }
    
    &:hover {
      .c-tooltip { opacity: 1; visibility: visible; transform: translateX(0); }
    }
    
    .c-tooltip {
      position: absolute; right: 60px;
      background: @text-main; color: @dark-bg;
      padding: 6px 12px; border-radius: 6px; font-size: 13px; font-weight: 600;
      white-space: nowrap;
      opacity: 0; visibility: hidden; transform: translateX(10px); transition: all 0.3s;
    }
  }
}

/* Back Top */
.back-top-btn {
  width: 50px; height: 50px;
  background: @card-bg; border: 1px solid @border; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: @text-main; font-size: 20px;
  transition: all 0.3s;
  
  &:hover { background: @primary-color; border-color: @primary-color; color: #fff; }
}

@media (max-width: 1024px) {
    .f-grid { flex-direction: column; }
    .f-links { flex-wrap: wrap; gap: 40px; }
    .use-cases-grid { grid-template-columns: repeat(2, 1fr); }
    .pricing-grid { flex-direction: column; align-items: center; }
    .price-card { width: 100%; max-width: 500px; }
    .testi-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 768px) {
  .desktop-only { display: none !important; }
  .mobile-only { display: flex !important; }
  
  .nav-container { padding: 10px; }
  .modern-nav { height: 56px; padding: 0 16px; border-radius: 12px; background: rgba(24, 24, 27, 0.9); }
  
  .product-hero { padding: 120px 0 60px; }
  .hero-content h1 { font-size: 40px; }
  .hero-subtitle { font-size: 16px; padding: 0; }
  .hero-actions { flex-direction: column; gap: 16px; button { width: 100%; } }
  
  .section-title { font-size: 32px; }
    .features-section { padding: 60px 24px; }
    
    .use-cases-grid { grid-template-columns: 1fr; }
    .stats-grid { flex-direction: column; gap: 40px; }
    
    .pricing-section { padding: 60px 16px; }
    .testimonials-section { padding: 60px 24px; }
  
  .f-links { gap: 30px; flex-direction: column; }
}
</style>
